import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components';
import type { CreateInvoiceItemPresetRequest, InvoiceItemPresetResponse } from '@/shared/api';
import { InvoiceItemPresetFields } from './InvoiceItemPresetFields';
import { parsePrice } from './parsePrice';
import { initialPresetState, type PresetFormState } from './presetFormState';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** null = neue Position, sonst Bearbeiten. */
  preset: InvoiceItemPresetResponse | null;
  saving: boolean;
  onSubmit: (values: CreateInvoiceItemPresetRequest) => void;
}

export function InvoiceItemPresetDialog({ open, onOpenChange, preset, saving, onSubmit }: Props) {
  const [form, setForm] = useState<PresetFormState>(() => initialPresetState(preset));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setForm(initialPresetState(preset));
      setError(null);
    }
  }, [open, preset]);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!form.label.trim()) {
      setError('Bitte eine Bezeichnung vergeben.');
      return;
    }
    const price = parsePrice(form.unitPrice);
    if (price === null) {
      setError('Bitte einen gültigen Preis eingeben.');
      return;
    }
    onSubmit({
      label: form.label.trim(),
      unitPrice: price,
      taxCategory: form.taxCategory,
      currency: null,
      isActive: form.isActive,
      notes: form.notes.trim() || null,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{preset ? 'Position bearbeiten' : 'Neue Position'}</DialogTitle>
          <DialogDescription>
            Gilt für den ganzen Workspace. Bereits ausgestellte Rechnungen bleiben unverändert.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <DialogBody className="space-y-4">
            <InvoiceItemPresetFields
              form={form}
              currency={preset?.currency ?? 'EUR'}
              onChange={setForm}
            />
            {error && (
              <p role="alert" className="text-sealwax text-xs">
                {error}
              </p>
            )}
          </DialogBody>
          <DialogFooter>
            <Button variant="ghost" type="button" onClick={() => onOpenChange(false)}>
              Abbrechen
            </Button>
            <Button variant="brand" type="submit" loading={saving}>
              Speichern
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
